import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Lock, Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import logo from '../../assets/logo.png';
import { API_ENDPOINTS } from '../../config/api';

const DEFAULT_SECURITY_PIN = process.env.REACT_APP_ADMIN_REGISTER_PIN || '202688';

const RegisterAdmin: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [securityPin, setSecurityPin] = useState(() => sessionStorage.getItem('khazanah_reg_pin_val') || '');
    const [pinInput, setPinInput] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [pinError, setPinError] = useState('');
    const [isPinVerified, setIsPinVerified] = useState(() => sessionStorage.getItem('khazanah_reg_pin_verified') === 'true');
    const [isLoading, setIsLoading] = useState(false);
    const [activeExpectedPin, setActiveExpectedPin] = useState(DEFAULT_SECURITY_PIN);
    const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetch(API_ENDPOINTS.settings)
            .then(res => res.json())
            .then(data => {
                if (data?.settings?.admin_register_pin) {
                    setActiveExpectedPin(data.settings.admin_register_pin);
                }
                if (data?.settings?.allow_admin_registration === 'false') {
                    setIsRegistrationAllowed(false);
                }
            })
            .catch(() => {});
    }, []);

    const handleVerifyPin = (e: React.FormEvent) => {
        e.preventDefault();
        setPinError('');

        if (pinInput.trim() === activeExpectedPin || pinInput.trim() === DEFAULT_SECURITY_PIN) {
            sessionStorage.setItem('khazanah_reg_pin_verified', 'true');
            sessionStorage.setItem('khazanah_reg_pin_val', pinInput.trim());
            setSecurityPin(pinInput.trim());
            setIsPinVerified(true);
            Swal.fire({
                icon: 'success',
                title: 'Akses Diterima',
                text: 'Silakan lengkapi formulir pendaftaran admin.',
                timer: 1200,
                showConfirmButton: false
            });
        } else {
            setPinError('PIN Keamanan salah! Akses pendaftaran ditolak.');
            Swal.fire({
                icon: 'error',
                title: 'PIN Salah!',
                text: 'PIN Keamanan tidak sesuai. Silakan tanyakan kepada Super Admin Khazanah.',
                confirmButtonColor: '#0F5B30'
            });
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const activePin = securityPin || sessionStorage.getItem('khazanah_reg_pin_val') || DEFAULT_SECURITY_PIN;

            const response = await fetch(`${API_ENDPOINTS.auth}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    name, 
                    email, 
                    password,
                    securityPin: activePin
                })
            });

            const data = await response.json();

            if (response.ok) {
                sessionStorage.removeItem('khazanah_reg_pin_verified');
                sessionStorage.removeItem('khazanah_reg_pin_val');
                Swal.fire({
                    icon: 'success',
                    title: 'Pendaftaran Berhasil!',
                    text: 'Akun Admin telah dibuat. Silakan login untuk melanjutkan.',
                    confirmButtonColor: '#18703E'
                }).then(() => {
                    navigate('/admin/login');
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Pendaftaran Gagal',
                    text: data.message || 'Terjadi kesalahan saat mendaftar.',
                    confirmButtonColor: '#18703E'
                });
            }
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Koneksi Terputus',
                text: 'Tidak dapat terhubung ke server backend.',
                confirmButtonColor: '#18703E'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center bg-[#FAF6EE] px-4 py-12 font-sans selection:bg-emerald/10">

            {/* Tombol Kembali ke Beranda */}
            <Link
                to="/"
                className="absolute top-6 left-6 md:top-10 md:left-10 flex items-center gap-2 text-gray-600 hover:text-emerald transition-colors font-medium bg-white px-5 py-2.5 rounded-full shadow-sm border border-gray-100 hover:shadow-md z-10 font-outfit text-sm"
            >
                <span className="text-xl leading-none">&larr;</span> Beranda
            </Link>

            {/* SCREEN 1: VERIFIKASI PIN KEAMANAN SEBELUM BISA DAFTAR */}
            {!isPinVerified ? (
                <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl shadow-black/5 w-full max-w-md border border-emerald-900/10 text-center animate-in fade-in zoom-in-95 duration-200">
                    <div className="w-16 h-16 bg-emerald/10 text-emerald rounded-2xl flex items-center justify-center mx-auto mb-6 border border-emerald/20">
                        <Lock size={30} />
                    </div>
                    
                    <h2 className="text-2xl font-bold font-outfit text-gray-900 mb-2">Verifikasi Akses Admin</h2>
                    <p className="text-gray-500 text-sm mb-8 font-outfit leading-relaxed">
                        Halaman ini dibatasi untuk staf internal. Masukkan <strong>PIN Keamanan</strong> untuk membuka formulir registrasi.
                    </p>

                    {!isRegistrationAllowed && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl text-xs text-left mb-6 flex items-start gap-2.5">
                            <AlertCircle size={18} className="shrink-0 mt-0.5" />
                            <div>
                                <strong>Pendaftaran Ditutup:</strong> Pendaftaran admin baru sedang dinonaktifkan oleh Super Administrator.
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleVerifyPin} className="space-y-5 text-left">
                        <div>
                            <label className="block text-xs font-outfit font-bold uppercase tracking-wider text-gray-600 mb-2">
                                PIN Keamanan Khazanah
                            </label>
                            <div className="relative">
                                <input
                                    type={showPin ? 'text' : 'password'}
                                    placeholder="Masukkan PIN"
                                    value={pinInput}
                                    onChange={(e) => {
                                        setPinInput(e.target.value);
                                        setPinError('');
                                    }}
                                    maxLength={10}
                                    autoFocus
                                    className={`w-full p-4 pr-12 border rounded-2xl focus:outline-none focus:ring-2 bg-gray-50 focus:bg-white transition-all font-outfit text-center text-lg tracking-widest ${
                                        pinError ? 'border-red-400 focus:ring-red-400' : 'border-gray-200 focus:ring-emerald'
                                    }`}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPin(!showPin)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                                >
                                    {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {pinError && (
                                <p className="text-red-500 text-xs mt-2 font-medium font-outfit">{pinError}</p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={!isRegistrationAllowed}
                            className="w-full bg-emerald hover:bg-emerald-dark text-white p-4 rounded-2xl font-outfit font-bold transition-all shadow-md flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>Buka Formulir Pendaftaran</span>
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between items-center text-xs font-outfit text-gray-500">
                        <Link to="/admin/login" className="hover:text-emerald font-semibold">
                            &larr; Sudah punya akun? Login
                        </Link>
                    </div>
                </div>
            ) : (
                /* SCREEN 2: FORMULIR REGISTRASI ADMIN */
                <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl shadow-black/5 w-full max-w-md border border-emerald-900/10 animate-in fade-in zoom-in-95 duration-200">
                    <div className="text-center mb-8">
                        <img
                            src={logo}
                            alt="Logo Khazanah"
                            className="h-14 mx-auto mb-4 object-contain"
                        />
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald/10 text-emerald text-xs font-bold font-outfit mb-3">
                            <ShieldCheck size={14} /> PIN Terverifikasi
                        </div>
                        <h2 className="text-2xl font-bold font-outfit text-gray-900">Daftar Admin Baru</h2>
                        <p className="text-gray-500 text-sm mt-1 font-outfit">Buat akun untuk mengakses Dashboard Khazanah</p>
                    </div>

                    <form onSubmit={handleRegister} className="space-y-5">
                        <div>
                            <label className="block text-xs font-outfit font-bold uppercase tracking-wider text-gray-600 mb-2">Nama Lengkap</label>
                            <input
                                type="text"
                                placeholder="Masukkan nama"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald bg-gray-50 focus:bg-white transition-all font-outfit"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-outfit font-bold uppercase tracking-wider text-gray-600 mb-2">Email</label>
                            <input
                                type="email"
                                placeholder="admin@khazanah.id"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald bg-gray-50 focus:bg-white transition-all font-outfit"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-outfit font-bold uppercase tracking-wider text-gray-600 mb-2">Password</label>
                            <input
                                type="password"
                                placeholder="Minimal 6 karakter"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald bg-gray-50 focus:bg-white transition-all font-outfit"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full text-white p-4 rounded-xl font-outfit font-bold transition-all shadow-md ${
                                isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald hover:bg-emerald-dark'
                            }`}
                        >
                            {isLoading ? 'Memproses Pendaftaran...' : 'Daftar Sekarang'}
                        </button>
                    </form>

                    <p className="text-center text-sm text-gray-500 mt-6 font-outfit">
                        Sudah punya akun?{' '}
                        <Link to="/admin/login" className="text-emerald font-bold hover:underline">
                            Login di sini
                        </Link>
                    </p>
                </div>
            )}
        </div>
    );
};

export default RegisterAdmin;